import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useDataProvider, useGetIdentity } from "@refinedev/core";
import { useParams } from "react-router";
import { parseAsString, useQueryState } from "nuqs";
import dayjs from "dayjs";
import {
  HealthMeterBreakdownResponse,
  HealthOverviewAllWindowsResponse,
  HealthTrendAllWindowsResponse,
} from "./components";
import { ZoomableMultiLineChart } from "../../components/charts/ZoomableMultiLineChart";
import {
  buildAliasMaps,
  MeterReadingsTable,
  type PersistedColumnConfig,
} from "../../components/meters/MeterReadingsTable";
import { CopyableText } from "../../components/common/CopyableText";
import { TimestampCell } from "../../components/common/TimestampCell";
import { splitMeterStatus } from "../../components/meters/readoutFormat";
import {
  encodeSortStateForQuery,
  extractSortStateFromTableSorter,
  isSameSortState,
  parseLegacySortStateFromQuery,
  parseSortStateFromQuery,
  toHeaderSortOrder,
  type SortState,
} from "../../components/meters/tableSortUrlState";

type UserIdentity = {
  isStaff?: boolean;
};

type MediaFilter = "all" | "electricity" | "water" | "heat";

type WindowMetrics = {
  degraded_count?: number;
  degraded_count_excluding_weekends?: number;
  missing?: number;
  missing_excluding_weekends?: number;
};

type MeterCombinedRow = {
  meter_id: number;
  meter_name: string;
  meter_uuid: string;
  media: string;
  address?: string | null;
  serial?: string | null;
  timestamp?: string | null;
  latest_readout?: {
    timestamp?: string | null;
    status?: string[];
    fields?: Record<string, unknown>;
    values?: Record<string, number>;
    is_late?: boolean;
  };
  windows: Record<string, WindowMetrics>;
};

type TrendCombinedRow = {
  snapshot_date: string;
  windows: Record<string, WindowMetrics>;
};

const LINE_COLORS = ["#1677ff", "#52c41a", "#fa8c16", "#eb2f96", "#13c2c2", "#722ed1"];
const BACKEND_ORDERABLE_FIELDS = new Set([
  "name",
  "uuid",
  "media",
  "address",
  "serial",
  "latest_snapshot_timestamp",
  "primary_value",
]);
const LEGACY_HEALTH_SORT_KEYS: Record<string, string> = {
  meter_name: "name",
  meter_uuid: "uuid",
};
const DEFAULT_METER_SORT: SortState = {
  key: "latest_snapshot_timestamp",
  order: "desc",
};

const areNumberArraysEqual = (left: number[], right: number[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
};

export const HealthDetailPage = (): JSX.Element => {
  const getDataProvider = useDataProvider();
  const { data: identity } = useGetIdentity<UserIdentity>();
  const { concentratorId } = useParams();
  const [snapshotDate, setSnapshotDate] = useQueryState(
    "date",
    parseAsString.withOptions({ history: "replace" })
  );
  const [meterSortQuery, setMeterSortQuery] = useQueryState(
    "s",
    parseAsString.withOptions({ history: "push" })
  );
  const [legacyMeterSortQuery, setLegacyMeterSortQuery] = useQueryState(
    "ms",
    parseAsString.withOptions({ history: "replace" })
  );
  const initialTrendEnd = snapshotDate ? dayjs(snapshotDate) : dayjs();
  const initialTrendRange: [dayjs.Dayjs, dayjs.Dayjs] = [
    initialTrendEnd.subtract(30, "days"),
    initialTrendEnd,
  ];

  const [meterLoading, setMeterLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [ignoreWeekends, setIgnoreWeekends] = useState(true);
  const [media, setMedia] = useState<MediaFilter>("all");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(initialTrendRange);
  const [initialDateRange, setInitialDateRange] =
    useState<[dayjs.Dayjs, dayjs.Dayjs]>(initialTrendRange);
  const [concentratorName, setConcentratorName] = useState<string>("");
  const [windows, setWindows] = useState<number[]>([]);
  const [meters, setMeters] = useState<MeterCombinedRow[]>([]);
  const [trendRows, setTrendRows] = useState<TrendCombinedRow[]>([]);

  const concentratorIdNumber = Number(concentratorId ?? 0);
  const validConcentrator = Number.isFinite(concentratorIdNumber) && concentratorIdNumber > 0;

  const sortableColumnKeys = useMemo(() => {
    const base = [
      "name",
      "uuid",
      "media",
      "address",
      "serial",
      "primary_value",
      "latest_snapshot_timestamp",
    ];
    const dynamicMissingColumns = windows.map((windowDays) => `missing_${windowDays}`);
    return [...base, ...dynamicMissingColumns];
  }, [windows]);

  const sortAliasConfig = useMemo(
    () => sortableColumnKeys.map((key) => ({ key, visible: true })),
    [sortableColumnKeys]
  );

  const { aliasByKey, keyByAlias } = useMemo(
    () => buildAliasMaps(sortAliasConfig),
    [sortAliasConfig]
  );

  const sortFromQuery = useMemo(
    () => parseSortStateFromQuery(meterSortQuery, keyByAlias),
    [keyByAlias, meterSortQuery]
  );

  const sortFromLegacyQuery = useMemo(() => {
    const parsedLegacy = parseLegacySortStateFromQuery(legacyMeterSortQuery);
    if (!parsedLegacy) {
      return null;
    }
    return {
      ...parsedLegacy,
      key: LEGACY_HEALTH_SORT_KEYS[parsedLegacy.key] ?? parsedLegacy.key,
    };
  }, [legacyMeterSortQuery]);

  useEffect(() => {
    if (!legacyMeterSortQuery) {
      return;
    }
    if (!sortFromQuery && sortFromLegacyQuery) {
      const migratedToken = encodeSortStateForQuery(sortFromLegacyQuery, aliasByKey);
      if (migratedToken) {
        void setMeterSortQuery(migratedToken, { history: "replace" });
      }
    }
    void setLegacyMeterSortQuery(null, { history: "replace" });
  }, [
    aliasByKey,
    legacyMeterSortQuery,
    setLegacyMeterSortQuery,
    setMeterSortQuery,
    sortFromLegacyQuery,
    sortFromQuery,
  ]);

  const meterSort = useMemo(() => {
    return sortFromQuery ?? sortFromLegacyQuery ?? DEFAULT_METER_SORT;
  }, [sortFromLegacyQuery, sortFromQuery]);

  const meterOrdering = useMemo(() => {
    if (!BACKEND_ORDERABLE_FIELDS.has(meterSort.key)) {
      return null;
    }
    const prefix = meterSort.order === "desc" ? "-" : "";
    return `${prefix}${meterSort.key}`;
  }, [meterSort.key, meterSort.order]);

  useEffect(() => {
    if (!validConcentrator) {
      return;
    }
    let cancelled = false;

    const loadOverview = async () => {
      try {
        const dataProvider = getDataProvider();
        const params = new URLSearchParams({ page_size: "200" });
        if (snapshotDate) {
          params.set("date", snapshotDate);
        }
        const { data } = (await dataProvider.custom?.({
          url: `/api/health/concentrators/${concentratorIdNumber}/overview/?${params.toString()}`,
          method: "get",
        })) || { data: null };
        const payload = data as HealthOverviewAllWindowsResponse | null;
        if (cancelled) {
          return;
        }

        const resolvedDate = payload?.snapshot_date ?? null;
        const resolvedWindows = (payload?.windows ?? []).slice().sort((a, b) => a - b);
        setWindows((previous) =>
          areNumberArraysEqual(previous, resolvedWindows) ? previous : resolvedWindows
        );

        if (!snapshotDate && resolvedDate) {
          void setSnapshotDate(resolvedDate);
        }

        const maybeName = payload?.concentrators?.[0]?.concentrator_name?.trim() ?? "";
        setConcentratorName(maybeName || `Concentrator #${concentratorIdNumber}`);
      } catch {
        if (!cancelled) {
          setConcentratorName(`Concentrator #${concentratorIdNumber}`);
        }
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [
    concentratorIdNumber,
    getDataProvider,
    snapshotDate,
    validConcentrator,
  ]);

  useEffect(() => {
    if (!validConcentrator) {
      return;
    }
    let cancelled = false;

    const loadMeterBreakdown = async () => {
      setMeterLoading(true);
      try {
        const dataProvider = getDataProvider();
        const params = new URLSearchParams({ page_size: "200" });
        if (snapshotDate) {
          params.set("date", snapshotDate);
        }
        if (media !== "all") {
          params.set("media", media);
        }
        if (meterOrdering) {
          params.set("ordering", meterOrdering);
        }

        const { data } = (await dataProvider.custom?.({
          url: `/api/health/concentrators/${concentratorIdNumber}/meters-breakdown/?${params.toString()}`,
          method: "get",
        })) || { data: null };
        const payload = data as HealthMeterBreakdownResponse | null;
        if (cancelled) {
          return;
        }

        const byMeter = new Map<number, MeterCombinedRow>();
        (payload?.meters?.results ?? []).forEach((row) => {
          byMeter.set(row.meter_id, {
            meter_id: row.meter_id,
            meter_name: row.meter_name,
            meter_uuid: row.meter_uuid,
            media: row.media,
            address: row.address,
            serial: row.serial,
            timestamp: row.timestamp,
            latest_readout: row.latest_readout,
            windows: row.windows,
          });
        });

        const mergedMeters = Array.from(byMeter.values());
        setMeters(mergedMeters);

        if (
          mergedMeters.length === 0 &&
          snapshotDate &&
          payload?.latest_available_date &&
          payload.latest_available_date !== snapshotDate
        ) {
          void setSnapshotDate(payload.latest_available_date);
        }
      } finally {
        if (!cancelled) {
          setMeterLoading(false);
        }
      }
    };

    loadMeterBreakdown();
    return () => {
      cancelled = true;
    };
  }, [
    concentratorIdNumber,
    getDataProvider,
    media,
    meterOrdering,
    snapshotDate,
    validConcentrator,
  ]);

  useEffect(() => {
    if (!validConcentrator) {
      return;
    }
    let cancelled = false;

    const loadTrend = async () => {
      setTrendLoading(true);
      try {
        const dataProvider = getDataProvider();
        const params = new URLSearchParams({
          start_date: dateRange[0].format("YYYY-MM-DD"),
          end_date: dateRange[1].format("YYYY-MM-DD"),
        });
        if (media !== "all") {
          params.set("media", media);
        }
        const { data } = (await dataProvider.custom?.({
          url: `/api/health/concentrators/${concentratorIdNumber}/trend/?${params.toString()}`,
          method: "get",
        })) || { data: null };
        const trendPayload = data as HealthTrendAllWindowsResponse | null;
        if (cancelled) {
          return;
        }

        const resolvedWindows = (trendPayload?.windows ?? []).slice().sort((a, b) => a - b);
        if (resolvedWindows.length > 0) {
          setWindows((previous) =>
            areNumberArraysEqual(previous, resolvedWindows) ? previous : resolvedWindows
          );
        }

        const byDate = new Map<string, TrendCombinedRow>();
        (trendPayload?.results ?? []).forEach((row) => {
          byDate.set(row.snapshot_date, {
            snapshot_date: row.snapshot_date,
            windows: row.windows,
          });
        });

        setTrendRows(
          Array.from(byDate.values()).sort((a, b) =>
            a.snapshot_date.localeCompare(b.snapshot_date)
          )
        );
      } finally {
        if (!cancelled) {
          setTrendLoading(false);
        }
      }
    };

    loadTrend();
    return () => {
      cancelled = true;
    };
  }, [concentratorIdNumber, dateRange, getDataProvider, media, validConcentrator]);

  const title = useMemo(() => {
    if (!validConcentrator) {
      return "Concentrator Health";
    }
    return concentratorName || `Concentrator #${concentratorIdNumber}`;
  }, [concentratorIdNumber, concentratorName, validConcentrator]);

  const meterColumns: ColumnsType<MeterCombinedRow> = useMemo(() => {
    const columns: ColumnsType<MeterCombinedRow> = [
      {
        title: "Meter",
        dataIndex: "meter_name",
        key: "name",
        ellipsis: true,
        sorter: true,
        render: (value: string) => <CopyableText text={value} />,
      },
      {
        title: "UUID",
        dataIndex: "meter_uuid",
        key: "uuid",
        ellipsis: true,
        sorter: true,
        render: (value: string) => (
          <CopyableText text={value} display={value ? `${value.slice(0, 8)}...` : undefined} />
        ),
      },
      {
        title: "Media",
        dataIndex: "media",
        key: "media",
        render: (v: string) => <Tag>{v}</Tag>,
        sorter: true,
      },
      {
        title: "Address",
        dataIndex: "address",
        key: "address",
        ellipsis: true,
        sorter: true,
        render: (value?: string | null) => value ?? "N/A",
      },
      {
        title: "Serial",
        dataIndex: "serial",
        key: "serial",
        ellipsis: true,
        sorter: true,
        render: (value?: string | null) => value ?? "N/A",
      },
      {
        title: "Primary",
        dataIndex: "latest_readout",
        key: "primary_value",
        sorter: true,
        render: (_value: unknown, row: MeterCombinedRow) => {
          const values = row.latest_readout?.values ?? {};
          const selected =
            row.media === "electricity"
              ? values.active
              : row.media === "water"
                ? values.water
                : row.media === "heat"
                  ? values.heat_kwh
                  : undefined;
          return selected == null ? "N/A" : selected.toFixed(2);
        },
      },
      {
        title: "Latest",
        dataIndex: "timestamp",
        key: "latest_snapshot_timestamp",
        sorter: true,
        render: (value?: string | null) => <TimestampCell value={value ?? ""} />,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (_: unknown, row: MeterCombinedRow) => {
          const split = splitMeterStatus(
            row.latest_readout?.status,
            row.latest_readout?.is_late
          );
          if (split.vendor.length > 0) {
            return <Tag color="gold">VENDOR</Tag>;
          }
          return <Tag color={split.isLate ? "red" : "green"}>{split.isLate ? "LATE" : "OK"}</Tag>;
        },
      },
    ];

    windows.forEach((windowDays) => {
      const key = String(windowDays);
      columns.push({
        title: `Missing ${windowDays}d`,
        key: `missing_${key}`,
        render: (_, row) =>
          ignoreWeekends
            ? row.windows[key]?.missing_excluding_weekends ?? 0
            : row.windows[key]?.missing ?? 0,
        sorter: (a, b) => {
          const left = ignoreWeekends
            ? a.windows[key]?.missing_excluding_weekends ?? 0
            : a.windows[key]?.missing ?? 0;
          const right = ignoreWeekends
            ? b.windows[key]?.missing_excluding_weekends ?? 0
            : b.windows[key]?.missing ?? 0;
          return left - right;
        },
      });
    });

    return columns.map((column) => {
      const columnKey = typeof column.key === "string" ? column.key : null;
      if (!columnKey || !("sorter" in column) || !column.sorter) {
        return column;
      }
      return {
        ...column,
        sortOrder: meterSort.key === columnKey ? toHeaderSortOrder(meterSort.order) : null,
      };
    });
  }, [ignoreWeekends, meterSort.key, meterSort.order, windows]);

  const meterTableDefaultColumns = useMemo<PersistedColumnConfig[]>(() => {
    const baseColumns: PersistedColumnConfig[] = [
      { key: "name", visible: true },
      { key: "uuid", visible: false },
      { key: "media", visible: true },
      { key: "address", visible: false },
      { key: "serial", visible: false },
      { key: "primary_value", visible: false },
      { key: "latest_snapshot_timestamp", visible: true },
      { key: "status", visible: false },
    ];

    const missingColumns = windows.map((windowDays) => ({
      key: `missing_${windowDays}`,
      visible: true,
    }));

    return [...baseColumns, ...missingColumns];
  }, [windows]);

  const trendData = useMemo(
    () =>
      trendRows.map((row) => {
        const points: Record<string, number | string> = {
          snapshot_date: row.snapshot_date,
          snapshot_ts: dayjs(row.snapshot_date).valueOf(),
        };
        windows.forEach((windowDays) => {
          const key = String(windowDays);
          points[`w_${key}`] = ignoreWeekends
            ? row.windows[key]?.degraded_count_excluding_weekends ?? 0
            : row.windows[key]?.degraded_count ?? 0;
        });
        return points;
      }),
    [ignoreWeekends, trendRows, windows]
  );

  const trendLines = useMemo(
    () =>
      windows.map((windowDays, index) => ({
        key: `w_${windowDays}`,
        color: LINE_COLORS[index % LINE_COLORS.length],
        name: `${windowDays}d`,
      })),
    [windows]
  );

  const handleZoomOut = () => {
    setDateRange(initialDateRange);
  };

  if (identity && !identity.isStaff) {
    return (
      <Alert
        type="error"
        showIcon
        message="Access denied"
        description="System Health is available only for staff users."
      />
    );
  }

  if (!validConcentrator) {
    return <Alert type="error" showIcon message="Invalid concentrator id." />;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Typography.Title level={2} style={{ marginBottom: 0 }}>
          {title}
        </Typography.Title>
      </Space>

      <Card>
        <Space wrap>
          <Typography.Text strong>Trend Range</Typography.Text>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(value) => {
              if (value && value[0] && value[1]) {
                const nextRange: [dayjs.Dayjs, dayjs.Dayjs] = [value[0], value[1]];
                setDateRange(nextRange);
                setInitialDateRange(nextRange);
              }
            }}
            format="YYYY-MM-DD"
            presets={[
              { label: "Last 7 Days", value: [dayjs().subtract(7, "days"), dayjs()] },
              { label: "Last 30 Days", value: [dayjs().subtract(30, "days"), dayjs()] },
              { label: "Last 3 Months", value: [dayjs().subtract(90, "days"), dayjs()] },
              { label: "Last Year", value: [dayjs().subtract(365, "days"), dayjs()] },
            ]}
          />
          <Typography.Text strong>Media</Typography.Text>
          <Select<MediaFilter>
            value={media}
            style={{ width: 180 }}
            onChange={setMedia}
            options={[
              { label: "All media", value: "all" },
              { label: "Electricity", value: "electricity" },
              { label: "Water", value: "water" },
              { label: "Heat", value: "heat" },
            ]}
          />
          <Checkbox
            checked={ignoreWeekends}
            onChange={(event) => setIgnoreWeekends(event.target.checked)}
          >
            Ignore weekends
          </Checkbox>
          <Button
            onClick={handleZoomOut}
            disabled={
              dateRange[0].isSame(initialDateRange[0], "day") &&
              dateRange[1].isSame(initialDateRange[1], "day")
            }
          >
            Zoom Out
          </Button>
        </Space>
      </Card>

      <Card title="Daily Degraded Trend" loading={trendLoading}>
        {trendData.length === 0 || trendLines.length === 0 ? (
          <Empty description="No trend data" />
        ) : (
          <ZoomableMultiLineChart
            data={trendData}
            xKey="snapshot_ts"
            lines={trendLines}
            onRangeSelect={(start, end) => {
              setDateRange([dayjs(start).startOf("day"), dayjs(end).endOf("day")]);
            }}
          />
        )}
      </Card>

      <Card
        title="Meter Breakdown"
        extra={
          <Space size="small">
            <Typography.Text strong>Snapshot Date</Typography.Text>
            <DatePicker
              value={snapshotDate ? dayjs(snapshotDate) : null}
              allowClear
              onChange={(value) => {
                const next = value ? value.format("YYYY-MM-DD") : null;
                void setSnapshotDate(next);
              }}
            />
          </Space>
        }
      >
        <MeterReadingsTable<MeterCombinedRow>
          tableKey={`health-meter-breakdown-${concentratorIdNumber}`}
          rowKey={(row) => row.meter_id}
          loading={meterLoading}
          columns={meterColumns}
          defaultColumns={meterTableDefaultColumns}
          columnStateMode="url"
          dataSource={meters}
          pagination={{ pageSize: 50 }}
          onChange={(_, __, sorter) => {
            const nextSortState = extractSortStateFromTableSorter<MeterCombinedRow>(sorter);
            const nextSortQuery =
              nextSortState && !isSameSortState(nextSortState, DEFAULT_METER_SORT)
                ? encodeSortStateForQuery(nextSortState, aliasByKey)
                : null;
            void setMeterSortQuery(nextSortQuery, { history: "push" });
          }}
        />
      </Card>
    </Space>
  );
};
